import ActionPanel from '@/components/ActionPanel';
import FetchingSelect from '@/components/FetchingSelect';
import PageContent from '@/components/PageContent';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { ThirdPartyType } from '@/models/third-party-account.model';
import { User } from '@/models/user.model';
import { fetchAndStoreStravaActivities, processStravaActivities } from '@/services/strava.service';
import { getSubscriptionPlansLookup, grantSubscriptionPlanToUser } from '@/services/subscription-plan.service';
import { hasUserThirdPartyAccount } from '@/services/third-party-account.service';
import { getUserStravaToken, getUserToken, sendTestPushNotification } from '@/services/user.service';
import { toastErrorMessage, toastSuccessMessage } from '@/utils/toasterMessage';
import { Icon } from '@rsuite/icons';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { MdContentCopy, MdPlayCircle } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { Button, Col, Input, InputGroup, Message, PanelGroup, Row, Stack, useToaster } from 'rsuite';



interface UserActionsProps{
    formObject?: User;
};

const UserActions: React.FC<UserActionsProps> = ({formObject}) => {
  const [loadingGetUserToken, setLoadingGetUserToken] = useState(false);
  const [loadingGetUserStravaToken, setLoadingGetUserStravaToken] = useState(false);
  const [userToken, setUserToken] = useState<string | undefined>(undefined);
  const [userStravaToken, setUserStravaToken] = useState<string | undefined>(undefined);
  const [loadingFetchUserStravaActivities, setLoadingFetchUserStravaActivities] = useState(false);
  const [hasUserStravaAccount, setHasUserStravaAccount] = useState(false);

  const [loadingSendTestPushNotification, setLoadingSendTestPushNotification] = useState(false);

  const [loadingGrantSubscriptionPlanToUser, setLoadingGrantSubscriptionPlanToUser] = useState(false);
  const [subscriptionPlanId, setSubscriptionPlanId] = useState<number | undefined>(undefined);

  const nav = useNavigate();
  const toaster = useToaster();

  useEffect(() => {
    if(formObject){
      hasUserThirdPartyAccount(formObject.id, ThirdPartyType.Strava)
        .then((res: BaseResponseModel<boolean>) => {
          if(res.success){
            setHasUserStravaAccount(res.result);
          }
        });
    }
  }, [formObject]);

  const handleGetUserToken = async () => {
    setLoadingGetUserToken(true);
    try {
      if(formObject){
        const result: BaseResponseModel<string> = await getUserToken(formObject.id);
        if (result.success) {
          setUserToken(result.result);
          toastSuccessMessage(`User Token Retrieved Successfully.`);
        }
      }
    } catch (err) {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in retrieving User Token:${errorModel?.errorMessage}`);
    }
    finally {
      setLoadingGetUserToken(false);
    }
  };
  const handleGetUserStravaToken = async () => {
    setLoadingGetUserStravaToken(true);
    try {
      if(formObject){
        const result: BaseResponseModel<string> = await getUserStravaToken(formObject.id);
        if (result.success) {
          setUserStravaToken(result.result);
          toastSuccessMessage(`User Strava Token Retrieved Successfully.`);
        }
      }
    } catch (err) {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in retrieving User Strava Token:${errorModel?.errorMessage}`);
    }
    finally {
      setLoadingGetUserStravaToken(false);
    }
  };

  const handleFetchUserStravaActivities = async () => {
    setLoadingFetchUserStravaActivities(true);
    try {
      if(formObject){
        const fetchRes: BaseResponseModel<number> = await fetchAndStoreStravaActivities(formObject.id, true, 200);
        if (fetchRes.success) {
          if(fetchRes.result > 0){
            toastSuccessMessage(`${fetchRes.result} older Activities is fetched and stored.`);
            const processRes: BaseResponseModel<boolean> = await processStravaActivities(formObject.id);
            if(processRes.success){
              toastSuccessMessage(`Activities are processed successfully.`);
            }
          }else {
            toastSuccessMessage(`None older activity exists to process.`);
          }
        }
      }
    } catch (err) {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching and processing strava activities:${errorModel?.errorMessage}`);
    }
    finally {
      setLoadingFetchUserStravaActivities(false);
    }
  };

  const handleSendTestPushNotification = async () => {
    setLoadingSendTestPushNotification(true);
    try {
      if(formObject){
        const result: BaseResponseModel<string> = await sendTestPushNotification(formObject.id);
        if (result.success) {
          toastSuccessMessage(`Test Push Notification sent successfully.`);
        }
      }
    } catch (err) {
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in sending Test Push Notification:${errorModel?.errorMessage}`);
    }
    finally {
      setLoadingSendTestPushNotification(false);
    }
  };

  const handleGrantSubscriptionPlanToUser = async () => {
    if(!formObject) return;
    
    const handleYesClick = async () => {
      setLoadingGrantSubscriptionPlanToUser(true);
      try {
        const res: BaseResponseModel<null> = await grantSubscriptionPlanToUser({ userId: formObject.id, planId: subscriptionPlanId});
        if (res.success) {
          toastSuccessMessage(`Subscription Plan granted to User successfully.`);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in Granting Subscription Plan To User:${errorModel?.errorMessage}`);
      }
      finally {
        setLoadingGrantSubscriptionPlanToUser(false);
        toaster.clear(); 
      }
    };

    const handleNoClick = () => {
      toaster.clear(); 
    };

    toaster.push(
      <Message type="warning" closable>
        <p style={{marginBottom:10}}>Are you sure you want to Grant <strong>Subscription Plan</strong>?</p>
        <div>
          <Button appearance="primary" onClick={handleYesClick}>Yes</Button>
          <Button appearance="subtle" onClick={handleNoClick} style={{ marginLeft:10}}>No</Button>
        </div>
      </Message>,
      { placement: 'topCenter', duration: 0 }
    );
  };


    return (
        <PageContent>
          <PanelGroup>
              <ActionPanel
                title='Get User Token' 
                description='This action retrieves the User Token. This token only use for API calls to fix issues.'
                onClick={handleGetUserToken}
                loading={loadingGetUserToken}
                doneContent={
                  <InputGroup inside>
                    <Input value={userToken} readOnly />
                    <InputGroup.Button onClick={() => navigator.clipboard.writeText(userToken || '')}>
                      <Icon as={MdContentCopy} />
                    </InputGroup.Button>
                  </InputGroup>
                }
              />
              <ActionPanel
                title='Get User Strava Token' 
                description='This action retrieves the User Strava Token.'
                onClick={handleGetUserStravaToken}
                loading={loadingGetUserStravaToken}
                disabled={!hasUserStravaAccount}
                doneContent={
                  <InputGroup inside>
                    <Input value={userStravaToken} readOnly />
                    <InputGroup.Button onClick={() => navigator.clipboard.writeText(userStravaToken || '')}>
                      <Icon as={MdContentCopy} />
                    </InputGroup.Button>
                  </InputGroup>
                }
              />
              <ActionPanel
                title='Fetch User Older Strava Activities' 
                description="This action fetched user's older strava activities via strava api and process them.
                Note: This action only enabled if user has connected his/her strava account."
                onClick={handleFetchUserStravaActivities}
                loading={loadingFetchUserStravaActivities}
                disabled={!hasUserStravaAccount}
              />
              <ActionPanel
                title='Send Test Push Notification' 
                description="This action sends a test push notification (title 'test', body 'this is a test notif') to this user's registered device only.
                Note: it fails if the user has no firebase token registered."
                onClick={handleSendTestPushNotification}
                loading={loadingSendTestPushNotification}
                disabled={!formObject}
              />
              <ActionPanel
                title='Grant Subscription Plan To User' 
                description="This action grant selected subscription plan to user."
                actionBar = {
                <Stack width='50%' spacing={6} justify='flex-end'>
                    <FetchingSelect block
                      fetchData={(text, page) => getSubscriptionPlansLookup(text, page)}
                      placeholder="Select a plan"
                      value={subscriptionPlanId}
                      onChange={val => setSubscriptionPlanId(val)}
                    />
                    <Button 
                        startIcon={<Icon as={MdPlayCircle} />} 
                        appearance="ghost" 
                        onClick={handleGrantSubscriptionPlanToUser}
                        disabled={!subscriptionPlanId}
                        loading={loadingGrantSubscriptionPlanToUser}
                    >Grant Plan</Button>                  
                </Stack>}
                onClick={handleGrantSubscriptionPlanToUser}
                loading={loadingGrantSubscriptionPlanToUser}
                disabled={!formObject}
              />
          </PanelGroup>
        </PageContent>
    );
};

export default UserActions;
